import React, { memo, forwardRef, useCallback } from 'react';
import {
  CheckmarkCircle16Filled,
  Mail16Regular,
  Alert16Regular,
  Document16Regular
} from '@fluentui/react-icons';

interface CheckboxProps {
  iconType?: 'email' | 'push' | 'terms';
  title: string;
  description: React.ReactNode;
  isChecked: boolean;
  onChange: () => void;
  testId: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { iconType = 'email', title, description, isChecked, onChange, testId },
    ref
  ) => {
    const getIcon = useCallback(() => {
      const iconProps = { className: 'w-4 h-4 text-green-600 mr-1.5' };

      switch (iconType) {
        case 'email':
          return <Mail16Regular {...iconProps} />;
        case 'push':
          return <Alert16Regular {...iconProps} />;
        case 'terms':
          return <Document16Regular {...iconProps} />;
        default:
          return <Mail16Regular {...iconProps} />;
      }
    }, [iconType]);

    return (
      <div
        className="flex items-center cursor-pointer group w-full text-left p-3 rounded-lg overflow-visible relative border border-green-100 bg-white hover:shadow-md transition-all min-h-[80px]"
        data-testid={testId}
        onClick={onChange}
        style={{
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 20
        }}
      >
        <div className="flex-grow">
          <div className="flex items-center">
            {getIcon()}
            <span className="text-sm text-green-800 font-medium">{title}</span>
          </div>
          <div className="mt-1 ml-6">
            <span className="text-xs text-green-700">{description}</span>
          </div>
        </div>

        <input
          type="checkbox"
          className="hidden"
          ref={ref}
          checked={isChecked}
          onChange={onChange}
        />
        <div
          className={`w-5 h-5 rounded-full border flex-shrink-0 flex items-center justify-center 
            ${isChecked ? 'bg-green-500' : 'bg-white'}
            border-green-500 ml-2 group-hover:border-green-600 transition-colors`}
          tabIndex={0}
          role="checkbox"
          aria-checked={isChecked}
          style={{
            position: 'relative',
            zIndex: 30,
            cursor: 'pointer',
            minWidth: '1.25rem' // 20px
          }}
        >
          {isChecked && (
            <CheckmarkCircle16Filled
              style={{
                fontSize: '0.75rem',
                color: 'white'
              }}
            />
          )}
        </div>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default memo(Checkbox);
